// ============================================================================
// TrainingWeekCard — one built or adjusted week, fixed height at any density
// A Mon-Sun load strip (stacked bars by sport, day total) carries the shape of
// the week; below it one-line session rows, changed sessions first, capped at
// five with an inline expander. Change grammar: added = teal +, moved =
// yellow arrow, removed = struck through and dim. Footer only on changes.
// ============================================================================

import { useState } from 'react'
import { Box, ButtonBase, Typography, useTheme } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { beoflowColors, typography, spacing, borderRadius } from '@/theme/muiTheme'
import type { RenderPayload, TrainingWeekSpec, TrainingWeekSessionSpec } from '@/types/chatRenders'
import { sportColors } from '@/theme/BeoflowSportColors.generated'
import CardShell, { monoMetaSx, kindLabelSx, actionLinkSx } from './CardShell'
import {
  addDaysIso,
  todayIso,
  sportDotColor,
  formatDuration,
  isoWeekNumber,
  formatWeekRange,
} from './cardUtils'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const ROW_CAP = 5
const BAR_HEIGHT = 44

const isRemoved = (s: TrainingWeekSessionSpec) => s.change === 'removed'

/** Changed sessions first, each group in day order (stable within a day). */
function orderSessionRows(sessions: TrainingWeekSessionSpec[]): TrainingWeekSessionSpec[] {
  return sessions
    .map((s, i) => ({ s, i }))
    .sort((a, b) => Number(!a.s.change) - Number(!b.s.change) || a.s.date.localeCompare(b.s.date) || a.i - b.i)
    .map(({ s }) => s)
}

/** Sport colour readable on this canvas: the `other` token is beoWhite,
 *  drawn for the dark canvas, and vanishes on a light card. */
function useSportFill() {
  const light = useTheme().palette.mode === 'light'
  return (sport: string | null | undefined) => {
    const color = sportDotColor(sport)
    return light && color === sportColors.other ? `${beoflowColors.graphGreyDark}80` : color
  }
}

/** Per-day minutes by sport, removed sessions excluded. */
function dayLoad(sessions: TrainingWeekSessionSpec[], date: string, fill: (sport: string) => string) {
  const bySport = new Map<string, number>()
  for (const s of sessions) {
    if (s.date !== date || isRemoved(s)) continue
    const color = fill(s.sport)
    bySport.set(color, (bySport.get(color) ?? 0) + (s.minutes ?? 0))
  }
  const total = [...bySport.values()].reduce((a, b) => a + b, 0)
  return { segments: [...bySport.entries()], total }
}

function ChangeMarker({ change }: { change: TrainingWeekSessionSpec['change'] }) {
  const light = useTheme().palette.mode === 'light'
  if (change !== 'added' && change !== 'moved') return null
  const added = change === 'added'
  // Base tones fail contrast on the white light-mode card; the Dark tones pass.
  const color = added
    ? light ? beoflowColors.tealDark : beoflowColors.tealBase
    : light ? beoflowColors.yellowDark : beoflowColors.yellowBase
  return (
    <Typography
      component="span"
      aria-hidden
      sx={{
        fontFamily: typography.fontFamilyMonospace,
        fontSize: '11px',
        lineHeight: 1,
        width: 10,
        textAlign: 'center',
        flexShrink: 0,
        color,
      }}
    >
      {added ? '+' : '→'}
    </Typography>
  )
}

/** 0 = Monday .. 6 = Sunday for an ISO date (YYYY-MM-DD), timezone-free. */
function isoWeekday(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return -1
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7
}

function mondayOf(iso: string): string {
  const wd = isoWeekday(iso)
  return wd < 0 ? iso : addDaysIso(iso.slice(0, 10), -wd)
}

function SessionRow({ session }: { session: TrainingWeekSessionSpec }) {
  const removed = isRemoved(session)
  const fill = useSportFill()
  // The row's own weekday, so a session outside the week still reads right.
  const dayIndex = session.date ? isoWeekday(session.date) : -1
  return (
    <Box
      data-testid="training-week-row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.sm,
        py: '4px',
        borderTop: 1,
        borderColor: 'divider',
        opacity: removed ? 0.5 : 1,
      }}
    >
      <Typography sx={{ ...kindLabelSx, color: 'text.secondary', width: 28, flexShrink: 0 }}>
        {DAY_LABELS[dayIndex] ?? ''}
      </Typography>
      <Box
        component="span"
        sx={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, backgroundColor: fill(session.sport) }}
      />
      <Typography
        noWrap
        data-testid="training-week-title"
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: typography.fontSizeXs,
          lineHeight: typography.lineHeightSm,
          ...(removed ? { textDecoration: 'line-through', color: 'text.secondary' } : {}),
        }}
      >
        {session.title}
        {session.change && <Box component="span" sx={visuallyHidden}>, {session.change}</Box>}
      </Typography>
      <Typography
        sx={{ ...monoMetaSx, flexShrink: 0, textTransform: 'none', ...(removed ? { textDecoration: 'line-through' } : {}) }}
      >
        {formatDuration(session.minutes)}
      </Typography>
      <ChangeMarker change={session.change} />
    </Box>
  )
}

export default function TrainingWeekCard({ render }: { render: RenderPayload }) {
  const [expanded, setExpanded] = useState(false)
  const fill = useSportFill()
  const spec = render.spec as unknown as Partial<TrainingWeekSpec>
  // Older backends sent the earliest session date, not a Monday: normalise so
  // the strip's MON..SUN columns are true weekdays.
  const weekStart = spec.week_start ? mondayOf(spec.week_start) : ''
  const sessions = Array.isArray(spec.sessions) ? spec.sessions : []
  const today = todayIso()

  const kept = sessions.filter(s => !isRemoved(s))
  const totalMinutes = kept.reduce((sum, s) => sum + (s.minutes ?? 0), 0)
  const weekNumber = weekStart ? isoWeekNumber(weekStart) : null
  const range = weekStart ? formatWeekRange(weekStart) : null

  const loads = DAY_LABELS.map((_, i) => dayLoad(sessions, addDaysIso(weekStart, i), fill))
  const busiest = Math.max(1, ...loads.map(l => l.total))

  const rows = orderSessionRows(sessions)
  const visibleRows = expanded ? rows : rows.slice(0, ROW_CAP)
  const hiddenCount = rows.length - visibleRows.length

  const counts = { added: 0, moved: 0, removed: 0 }
  for (const s of sessions) if (s.change) counts[s.change]++
  const hasChanges = counts.added + counts.moved + counts.removed > 0

  const sectionPx = { px: spacing.md }

  return (
    <CardShell
      kindLabel={range ? `Training week · ${range}` : 'Training week'}
      action={{ label: 'Open plan', to: '/calendar' }}
      disableBodyPadding
    >
      {/* Meta: week number, volume, session count (removed sessions excluded) */}
      <Box sx={{ ...sectionPx, py: '10px', display: 'flex', alignItems: 'baseline', gap: spacing.md, flexWrap: 'wrap' }}>
        {weekNumber && (
          <Typography sx={{ fontSize: typography.fontSizeLg, fontWeight: typography.fontWeightSemibold, lineHeight: typography.lineHeightSm }}>
            Week {weekNumber}
          </Typography>
        )}
        {totalMinutes > 0 && (
          <Typography sx={monoMetaSx}>
            <Box component="span" sx={{ color: 'text.primary', fontWeight: typography.fontWeightSemibold }}>
              {formatDuration(totalMinutes)}
            </Box>{' '}
            volume
          </Typography>
        )}
        <Typography sx={monoMetaSx}>
          <Box component="span" sx={{ color: 'text.primary', fontWeight: typography.fontWeightSemibold }}>
            {kept.length}
          </Box>{' '}
          session{kept.length === 1 ? '' : 's'}
        </Typography>
      </Box>

      {/* Reason line with a yellow ! ring */}
      {spec.reason && (
        <Box sx={{ ...sectionPx, display: 'flex', alignItems: 'center', gap: spacing.sm, pb: spacing.sm }}>
          <Box
            component="span"
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              border: `1.5px solid ${beoflowColors.yellowBase}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontFamily: typography.fontFamilyMonospace,
              fontSize: '9px',
              color: beoflowColors.yellowBase,
              lineHeight: 1,
            }}
          >
            !
          </Box>
          <Typography sx={{ fontSize: typography.fontSizeXs, color: 'text.secondary' }}>{spec.reason}</Typography>
        </Box>
      )}

      {/* Load strip: stacked bars by sport, scaled to the busiest day */}
      <Box
        sx={{
          ...sectionPx,
          pt: spacing.sm,
          pb: '10px',
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '6px',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        {loads.map((load, i) => {
          const isToday = addDaysIso(weekStart, i) === today
          return (
            <Box key={DAY_LABELS[i]} data-testid="training-week-day" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: spacing.xs, minWidth: 0 }}>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 34,
                  height: BAR_HEIGHT,
                  borderRadius: borderRadius.small,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  backgroundColor: 'action.hover',
                }}
              >
                {load.segments.map(([color, minutes]) => (
                  <Box key={color} sx={{ height: `${(minutes / busiest) * 100}%`, backgroundColor: color, flexShrink: 0 }} />
                ))}
              </Box>
              <Typography noWrap sx={{ ...monoMetaSx, fontSize: '9px', textTransform: 'none', color: 'text.primary', minHeight: 13 }}>
                {load.total ? formatDuration(load.total) : ''}
              </Typography>
              <Typography sx={{ ...kindLabelSx, color: isToday ? beoflowColors.meanBlue : 'text.secondary' }}>
                {DAY_LABELS[i]}
              </Typography>
            </Box>
          )
        })}
      </Box>

      {/* Session rows: changed first, capped with an inline expander */}
      {rows.length > 0 && (
        <Box sx={{ ...sectionPx, pb: spacing.xs }}>
          {visibleRows.map((s, j) => (
            <SessionRow key={`${s.date}-${s.title}-${j}`} session={s} />
          ))}
          {hiddenCount > 0 && (
            <ButtonBase onClick={() => setExpanded(true)} sx={{ ...actionLinkSx, fontSize: '9px', pt: '6px', pb: '2px' }}>
              + {hiddenCount} more session{hiddenCount === 1 ? '' : 's'}
            </ButtonBase>
          )}
        </Box>
      )}

      {/* Footer only when changes exist */}
      {hasChanges && (
        <Box sx={{ ...sectionPx, py: spacing.sm, borderTop: 1, borderColor: 'divider', display: 'flex', gap: spacing.sm }}>
          {counts.added > 0 && <Typography sx={monoMetaSx}>{counts.added} added</Typography>}
          {counts.moved > 0 && <Typography sx={monoMetaSx}>{counts.moved} moved</Typography>}
          {counts.removed > 0 && <Typography sx={monoMetaSx}>{counts.removed} removed</Typography>}
        </Box>
      )}
    </CardShell>
  )
}
